/*
 * Copyright (c) 2022 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useRef, useState, memo, useMemo, useEffect } from 'react';
import { useResizeObserver } from 'hooks/useResizeObserver';
import NavigationArrow, { ArrowOrientation } from './NavigationArrow';

interface Props {
  cards: JSX.Element[];
  large?: boolean;
}

function RailSlider({ cards, large }: Props) {
  const [scrollValue, setScrollValue] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);

  const railRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const parentDimensions = useResizeObserver(railRef);

  const getPaddingValue = useCallback(() => {
    // TODO centralize this values
    if (window.innerWidth >= 1920) {
      return 240;
    }
    return 120;
  }, []);

  const handleRightArrowNavigation = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      let leftValue = 0;

      if (railRef.current && containerRef.current?.children) {
        for (let i = 0; i < containerRef.current.children.length; i += 1) {
          const el = containerRef.current.children[i] as HTMLElement;
          if (containerRef.current.clientWidth + containerRef.current.scrollLeft < el.offsetLeft) {
            leftValue = el.offsetLeft - el.clientWidth - getPaddingValue() - containerRef.current.scrollLeft;
            break;
          }
          if (containerRef.current.children.length - i === 1) {
            leftValue = containerRef.current.clientWidth - railRef.current.scrollLeft;
            break;
          }
        }

        containerRef.current.scrollBy({
          left: leftValue,
          behavior: 'smooth',
        });
        event.stopPropagation();
      }
    },
    [getPaddingValue]
  );

  const handleLeftArrowNavigation = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      let leftValue = 0;
      if (containerRef.current?.children) {
        const parentElement = containerRef.current;
        const children = Array.from(containerRef.current.children);

        // Get the first element that crosses the left axis
        const reversedChildren = [...children].reverse();
        const lastCardToShow = reversedChildren.find(
          (card) => (card as HTMLElement).offsetLeft < parentElement.scrollLeft + getPaddingValue()
        );

        // Calculate the max position we can scroll to that keeps the calculated element fully inside lift and right axis
        const maxLeft =
          (lastCardToShow as HTMLElement).offsetLeft - (parentElement.clientWidth - 2 * getPaddingValue());

        // Calculate the fist card on the left of the calculated position
        const firstCardToShow = children.find((card) => (card as HTMLElement).offsetLeft >= maxLeft);

        // Set the scroll position to that card minus the offset axis so the card aligns with it
        leftValue = (firstCardToShow as HTMLElement).offsetLeft - getPaddingValue();

        containerRef.current.scrollTo({
          left: leftValue,
          behavior: 'smooth',
        });
      }
      event.stopPropagation();
    },
    [getPaddingValue]
  );
  const handlesScroll = useCallback(() => {
    if (containerRef.current) {
      setScrollValue(containerRef.current?.scrollLeft);
    }
  }, []);

  const gradientStyle = useMemo(() => {
    if (scrollValue !== 0) {
      if (scrollWidth - scrollValue !== parentDimensions.width) {
        return 'both-mask';
      }
      return 'left-mask';
    }
    return 'right-mask';
  }, [parentDimensions.width, scrollValue, scrollWidth]);

  useEffect(() => {
    if (containerRef.current) {
      setScrollWidth(containerRef.current?.scrollWidth);
      setScrollValue(containerRef.current?.scrollLeft);
    }
  }, [parentDimensions]);

  return (
    <div className="rail-slider" ref={railRef}>
      {scrollValue !== 0 && <NavigationArrow orientation={ArrowOrientation.LEFT} onClick={handleLeftArrowNavigation} />}
      <div
        className={`rail-slider-container ${large ? 'large' : ''} ${gradientStyle}`}
        ref={containerRef}
        onScroll={handlesScroll}
      >
        {cards}
      </div>
      {scrollWidth - scrollValue !== Math.round(parentDimensions.width) && scrollWidth > 0 && (
        <NavigationArrow orientation={ArrowOrientation.RIGHT} onClick={handleRightArrowNavigation} />
      )}
    </div>
  );
}

export default memo(RailSlider);
